/**
 * MFA Setup Component
 * Epic #87: Core Platform MVP - MFA TOTP Setup
 * Carbon Design System implementation with backend integration
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ProgressIndicator,
  ProgressStep,
  Tile,
  Button,
  TextInput,
  InlineNotification,
  Stack,
  Grid,
  Column,
  CodeSnippet,
  Loading,
} from '@carbon/react';
import { Checkmark, Copy, Download, Security } from '@carbon/icons-react';
import QRCode from 'qrcode.react';
import axios from 'axios';
import styles from './MFASetup.module.scss';

interface MFASetupProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (success: boolean) => void;
  userId: string;
  userEmail: string;
}

interface SetupData {
  secret: string;
  otpauth_uri: string;
  qr_code_data: string;
  manual_entry_key: string;
}

interface BackupCodesData {
  backup_codes: string[];
  success: boolean;
  mfa_enabled: boolean;
  security_score: number;
}

export const MFASetup: React.FC<MFASetupProps> = ({ isOpen, onClose, onComplete, userId, userEmail }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const API_BASE_URL = process.env.VITE_API_BASE_URL || '/api';

  // Step 0: Introduction
  // Step 1: QR Code Display
  // Step 2: Verification
  // Step 3: Backup Codes
  // Step 4: Success

  // Initialize MFA setup when modal opens
  useEffect(() => {
    if (isOpen && currentStep === 1 && !setupData) {
      initiateMFASetup();
    }
  }, [isOpen, currentStep]);

  const initiateMFASetup = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/auth/mfa/setup`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSetupData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to initialize MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/auth/mfa/verify`,
        { code: verificationCode },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data: BackupCodesData = response.data.data;
      setBackupCodes(data.backup_codes);
      setSuccess('MFA enabled successfully!');
      setCurrentStep(3); // Move to backup codes step
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(null), 2000);
  };

  const downloadBackupCodes = () => {
    const content = `Sigma08 MFA Backup Codes\n\nAccount: ${userEmail}\nGenerated: ${new Date().toLocaleString()}\n\n${backupCodes.join('\n')}\n\nIMPORTANT:\n- Store these codes in a safe place\n- Each code can only be used once\n- Use these codes if you lose access to your authenticator app`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sigma08-mfa-backup-codes-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    setSuccess('Backup codes downloaded!');
    setTimeout(() => setSuccess(null), 2000);
  };

  const handleComplete = () => {
    onComplete(true);
    onClose();
  };

  const handleCancel = () => {
    if (currentStep >= 3) {
      // If backup codes have been generated, require confirmation
      if (window.confirm('Are you sure? Make sure you have saved your backup codes.')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className={styles.mfaIntro}>
            <h3 className={styles.stepTitle}>Enhance Your Account Security</h3>

            <InlineNotification
              kind="info"
              title="Two-Factor Authentication (2FA)"
              subtitle="Add an extra layer of security to your account. Even if someone discovers your password, they won't be able to access your account without your second factor."
              lowContrast
              className={styles.notificationWrapper}
            />

            <div className={styles.requirementsSection}>
              <h4 className={styles.sectionHeading}>What you'll need:</h4>
              <ul className={styles.requirementsList}>
                <li>An authenticator app (Google Authenticator, Microsoft Authenticator, Authy, or 1Password)</li>
                <li>Your mobile device</li>
                <li>About 5 minutes</li>
              </ul>
            </div>

            <div className={styles.requirementsSection}>
              <h4 className={styles.sectionHeading}>Setup steps:</h4>
              <ol className={styles.stepsList}>
                <li>Scan a QR code with your authenticator app</li>
                <li>Enter the 6-digit code to verify</li>
                <li>Save your backup codes</li>
              </ol>
            </div>

            <Stack gap={5} className={styles.buttonGroup}>
              <Button kind="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button kind="primary" onClick={() => setCurrentStep(1)} renderIcon={Security}>
                Begin Setup
              </Button>
            </Stack>
          </div>
        );

      case 1:
        return (
          <div className={styles.mfaQrCode}>
            <h3 className={styles.stepTitle}>Scan QR Code</h3>

            {loading && !setupData && (
              <div className={styles.loadingContainer}>
                <Loading description="Generating QR code..." withOverlay={false} />
              </div>
            )}

            {setupData && (
              <Grid>
                <Column lg={8} md={8} sm={4}>
                  <div className={styles.instructionSection}>
                    <h4 className={styles.sectionHeading}>Step 1: Open your authenticator app</h4>
                    <p className={styles.instructionText}>
                      If you don't have one, download Google Authenticator, Microsoft Authenticator, Authy, or 1Password
                      from your app store.
                    </p>
                  </div>

                  <div>
                    <h4 className={styles.sectionHeading}>Step 2: Scan this QR code</h4>
                    <p className={styles.instructionText}>
                      In your authenticator app, choose "Add account" or "Scan QR code", then scan the code on the
                      right.
                    </p>
                  </div>
                </Column>

                <Column lg={8} md={8} sm={4}>
                  <Tile className={styles.qrCodeTile}>
                    <QRCode value={setupData.qr_code_data} size={200} level="H" includeMargin={true} />

                    <div className={styles.manualEntrySection}>
                      <p className={styles.manualEntryLabel}>Can't scan? Enter this code manually:</p>
                      <CodeSnippet
                        type="single"
                        feedback="Copied to clipboard"
                        onClick={() => copyToClipboard(setupData.manual_entry_key)}
                      >
                        {setupData.manual_entry_key}
                      </CodeSnippet>
                    </div>
                  </Tile>
                </Column>
              </Grid>
            )}

            {error && (
              <InlineNotification
                kind="error"
                title="Error"
                subtitle={error}
                lowContrast
                className={styles.errorNotification}
                onCloseButtonClick={() => setError(null)}
              />
            )}

            <Stack gap={5} className={styles.buttonGroup}>
              <Button kind="secondary" onClick={() => setCurrentStep(0)} disabled={loading}>
                Back
              </Button>
              <Button kind="primary" onClick={() => setCurrentStep(2)} disabled={loading || !setupData}>
                Continue to Verification
              </Button>
            </Stack>
          </div>
        );

      case 2:
        return (
          <div className={styles.mfaVerification}>
            <h3 className={styles.stepTitle}>Verify Setup</h3>

            <InlineNotification
              kind="info"
              title="Enter Verification Code"
              subtitle="Open your authenticator app and enter the 6-digit code displayed for Sigma08."
              lowContrast
              className={styles.notificationWrapper}
            />

            <div className={styles.verificationInputContainer}>
              <TextInput
                id="verification-code"
                labelText="6-Digit Code"
                placeholder="000000"
                value={verificationCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(value);
                }}
                maxLength={6}
                disabled={loading}
                invalid={error !== null}
                invalidText={error || ''}
                className={styles.verificationInput}
              />

              {success && (
                <InlineNotification
                  kind="success"
                  title="Success"
                  subtitle={success}
                  lowContrast
                  className={styles.successNotification}
                  hideCloseButton
                />
              )}
            </div>

            <Stack gap={5} className={styles.buttonGroup}>
              <Button kind="secondary" onClick={() => setCurrentStep(1)} disabled={loading}>
                Back
              </Button>
              <Button
                kind="primary"
                onClick={handleVerification}
                disabled={loading || verificationCode.length !== 6}
                renderIcon={Checkmark}
              >
                {loading ? 'Verifying...' : 'Verify and Enable MFA'}
              </Button>
            </Stack>
          </div>
        );

      case 3:
        return (
          <div className={styles.mfaBackupCodes}>
            <h3 className={styles.stepTitle}>Save Your Backup Codes</h3>

            <InlineNotification
              kind="success"
              title="MFA Enabled Successfully!"
              subtitle="Your account is now protected with two-factor authentication."
              lowContrast
              className={styles.notificationWrapper}
            />

            <InlineNotification
              kind="warning"
              title="Important: Save These Backup Codes"
              subtitle="Use these codes to access your account if you lose access to your authenticator app. Each code can only be used once."
              lowContrast
              className={styles.notificationWrapper}
            />

            <Tile className={styles.backupCodesTile}>
              <Grid condensed>
                {backupCodes.map((code, index) => (
                  <Column key={index} lg={4} md={4} sm={2} className={styles.backupCodeColumn}>
                    <CodeSnippet type="single" feedback="Copied!" onClick={() => copyToClipboard(code)}>
                      {code}
                    </CodeSnippet>
                  </Column>
                ))}
              </Grid>
            </Tile>

            <Stack gap={5} className={styles.downloadButtonGroup}>
              <Button kind="secondary" renderIcon={Copy} onClick={() => copyToClipboard(backupCodes.join('\n'))}>
                Copy All Codes
              </Button>
              <Button kind="secondary" renderIcon={Download} onClick={downloadBackupCodes}>
                Download Codes
              </Button>
            </Stack>

            <Tile className={styles.securityTile}>
              <Stack gap={3}>
                <Security size={24} className={styles.securityIcon} />
                <div>
                  <h4 className={styles.securityHeading}>Your Account Security: Excellent</h4>
                  <p className={styles.securityText}>
                    Two-factor authentication provides the highest level of account protection.
                  </p>
                </div>
              </Stack>
            </Tile>

            <Stack gap={5} className={styles.completeButtonGroup}>
              <Button kind="primary" onClick={handleComplete} renderIcon={Checkmark}>
                Complete Setup
              </Button>
            </Stack>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      open={isOpen}
      onRequestClose={handleCancel}
      modalHeading="Two-Factor Authentication Setup"
      passiveModal={currentStep === 3}
      primaryButtonText={currentStep === 3 ? 'Complete Setup' : undefined}
      secondaryButtonText={currentStep === 3 ? undefined : 'Cancel'}
      onRequestSubmit={currentStep === 3 ? handleComplete : undefined}
      onSecondarySubmit={handleCancel}
      size="lg"
    >
      <div className={styles.modalContainer}>
        <ProgressIndicator currentIndex={currentStep} spaceEqually className={styles.progressIndicator}>
          <ProgressStep label="Introduction" />
          <ProgressStep label="Scan QR Code" />
          <ProgressStep label="Verify" />
          <ProgressStep label="Backup Codes" />
        </ProgressIndicator>

        {renderStepContent()}
      </div>
    </Modal>
  );
};

export default MFASetup;
